/**
 * Onboarding wizard — registers a new channel with enough fidelity that a
 * brand can reproduce its existing video system: brand colors (caption +
 * accent + speaker name), an uploaded brand font, the logo + its geometry,
 * and caption layout (position, size, single-vs-two-line, background box).
 *
 * Flow: basics → colors → logo → font → captions → pipelines → voice →
 * buffer → review → POST /api/channels → activate → (connect Buffer) → reload.
 *
 * The Buffer step is optional and writes the token to the NEW channel's own
 * credentials file (connectChannelScheduling), so a brand's account is linked
 * at onboarding time. It can still be connected later in Settings → Scheduling.
 */
import { ChevronLeft, ChevronRight, Upload, Check, Type, Video, Captions, ScanSearch, Search, FileImage, Sparkles, Lock, Loader2, RotateCcw, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createChannelApi, activateChannelApi, archiveChannelApi, connectChannelScheduling, analyzeReferenceApi, getChannelApi, updateChannelFullApi, fetchChannelLogoObjectUrl, fetchFontObjectUrl, listFontFamiliesApi, type CreateChannelInput, type EditChannelInput, type FontFamilyEntry, type StylingProfile } from "../api";
import {
  CAPTION_UNSETTABLE,
  SPEAKER_CARD_UNSETTABLE,
  withoutUnset,
  type EditPatch,
} from "../../../src/lib/clipEditState.ts";
import type { FontFaceSpec, RenderLayout } from "../../../src/remotion/types.ts";
import { buildSampleInputProps } from "../lib/onboardingSample";
import { clearOnboardingDesignDraft, OnboardingStudio } from "./OnboardingStudio";
import { BufferExplainer } from "../components/BufferExplainer";
import { ChannelPreview } from "../components/ChannelPreview";
import { PageHeader } from "../components/PageHeader";
import { cardStyle, ctaBtnStyle, inputStyle, fieldLabelStyle, ctaSmallStyle } from "../components/styles";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { LANGUAGE_PRESETS, DEFAULT_LANGUAGE, makeCustomLanguage, directionForCode, scriptName, type LanguagePreset } from "../lib/languages";
import { useIsMobile } from "../lib/useIsMobile";

const STEPS = [
  { id: "basics", label: "Basics" },
  { id: "language", label: "Language" },
  { id: "reference", label: "Reference" },
  { id: "colors", label: "Colors" },
  { id: "font", label: "Font" },
  // The Design step IS the real Studio over a live sample clip (spec
  // 2026-07-18 §4) — it replaced the static drag canvas + Preview step.
  { id: "layout", label: "Design" },
  { id: "features", label: "Pipelines" },
  { id: "voice", label: "Voice" },
  { id: "buffer", label: "Buffer" },
  { id: "review", label: "Review" },
] as const;

type StepId = typeof STEPS[number]["id"];

/** Founding font families every channel may assign per element. */
const FOUNDING_FAMILIES = ["AlHurra", "Lalezar", "Faseh"] as const;

interface OnboardProps {
  onDone: (slug: string) => void;
  /** When set, the wizard EDITS this existing channel instead of creating a new
   *  one: it pre-fills from the saved config and Save updates it in place. */
  editSlug?: string;
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9-\s]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.readAsDataURL(file);
  });
}

/**
 * Founding (Quotologies) defaults for every field the reference-vision pass can
 * pre-fill. Single source of truth: the wizard's initial state AND each matched
 * field's per-field "reset" both read from here, so a reset always returns the
 * field to exactly the value onboarding started at.
 */
const DEFAULTS: {
  captionColor: string; captionAccent: string; speakerNameColor: string;
  capUniform: boolean; capFontSizePx: number; capTopPx: number; capPunchPx: number; capBottomPx: number;
  logoWidthPct: number; logoBottomPx: number; logoXPct: number;
  speakerCardXPct: number; speakerCardYPct: number;
} = {
  captionColor: "#F2EBD8",
  captionAccent: "#FFBF17",
  speakerNameColor: "#FFBF17",
  capUniform: false,
  capFontSizePx: 115,
  // Two-line sizes are INDEPENDENT (no derived multiplier). Defaults reproduce the
  // founding 0.95× / 1.9× at the 115px base, so an untouched channel is unchanged.
  capTopPx: 109,
  capPunchPx: 219,
  capBottomPx: 240,
  logoWidthPct: 36,
  logoBottomPx: 620,
  logoXPct: 50,
  speakerCardXPct: 6.0185,
  speakerCardYPct: 8.333,
};

/** Normalize a model-supplied hex ("f2ebd8" / "#F2EBD8") to "#RRGGBB", or null. */
function normHex(s: string | undefined): string | null {
  if (!s) return null;
  const t = ("#" + s.trim().replace(/^#/, "")).toUpperCase();
  return /^#[0-9A-F]{6}$/.test(t) ? t : null;
}

/**
 * Reference caption placement → `capBottomPx` (distance from the 3840px canvas
 * bottom). DELIBERATELY conservative: the render centres captions
 * (justify:"center") so bottomPx only nudges vertical position — large values
 * would drift the preview from the render. These are safe starting points the
 * operator fine-tunes by dragging on the Captions/Preview step.
 */
function captionPlacementToBottomPx(p: NonNullable<StylingProfile["caption"]>["placement"]): number {
  switch (p) {
    case "top": return 1400;
    case "center": return 900;
    case "lower-third": return 500;
    default: return DEFAULTS.capBottomPx; // "bottom"
  }
}

/** Coarse caption size bucket → px (founding one-line size = 115). */
function fontScaleToPx(s: NonNullable<StylingProfile["caption"]>["approxFontScale"]): number {
  return s === "small" ? 95 : s === "large" ? 150 : DEFAULTS.capFontSizePx;
}

/** Coarse logo size bucket → width % of canvas (founding = 36). */
function logoSizeToWidthPct(s: NonNullable<StylingProfile["logo"]>["approxSize"]): number {
  return s === "small" ? 22 : s === "large" ? 52 : DEFAULTS.logoWidthPct;
}

/** Logo corner → horizontal centre % + distance-from-bottom px (editor clamps
 *  logo bottomPx to [0,2000], so "top" maps to that ceiling). */
function logoCornerToPos(c: NonNullable<StylingProfile["logo"]>["placement"]): { xPct: number; bottomPx: number } {
  const cc = c ?? "bottom-center";
  const xPct = cc.endsWith("left") ? 15 : cc.endsWith("right") ? 85 : 50;
  const bottomPx = cc.startsWith("top") ? 2000 : 380;
  return { xPct, bottomPx };
}

/** Speaker-card corner → top-left anchor %, clamped away from the edges (the
 *  card is a wide name+role block, so "center"/"right" are pulled inward). */
function speakerCornerToPos(c: NonNullable<StylingProfile["speakerCard"]>["placement"]): { xPct: number; yPct: number } {
  const cc = c ?? "top-left";
  const xPct = cc.endsWith("left") ? 6 : cc.endsWith("right") ? 62 : 34;
  const yPct = cc.startsWith("top") ? 8 : 82;
  return { xPct, yPct };
}

/** Human sentence describing the reference's font feel (a SUGGESTION only — the
 *  wizard never auto-picks a font file; the operator uploads a match). */
function describeFontSuggestion(f: NonNullable<StylingProfile["font"]>): string {
  const bits = [
    f.classification ? `a ${f.classification} face` : "",
    f.weight ? `${f.weight} weight` : "",
    f.note ? `“${f.note}”` : "",
  ].filter(Boolean).join(", ");
  return bits ? `Your reference font reads as ${bits}. Upload a matching .ttf/.otf below for the closest result.` : "";
}

/** Which wizard step a matched field belongs to (drives per-step panels). */
type MatchScope = "colors" | "captions" | "logo" | "speaker" | "voice";

/** One field pre-filled from the reference, shown as a resettable chip. */
interface MatchedField {
  key: string;
  scope: MatchScope;
  label: string;
  /** Human-readable detected value, e.g. "#FFBF17" or "lower-third". */
  detected: string;
  /** Optional color swatch (for color fields). */
  swatch?: string;
  /** Revert this one field to its founding default. */
  revert: () => void;
}

export function Onboard({ onDone, editSlug }: OnboardProps): React.JSX.Element {
  const isMobile = useIsMobile();
  const isEdit = !!editSlug;
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveTyped, setArchiveTyped] = useState("");
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [stepIdx, setStepIdx] = useState(0);
  const stepId: StepId = STEPS[stepIdx].id;
  // Edit mode: the saved font family (kept unless a new font is uploaded). The
  // wizard can't reconstruct the original .ttf data URL, so we show it by name.
  const [existingFontFamily, setExistingFontFamily] = useState<string | null>(null);
  const [editLoading, setEditLoading] = useState(false);

  // basics
  const [displayName, setDisplayName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  // language (default Arabic → stored as "no language field" = byte-identical V1)
  const [language, setLanguage] = useState<LanguagePreset>(DEFAULT_LANGUAGE);
  // "Other" custom language: name + code the operator types. Direction seeds
  // from the code (directionForCode) but is operator-overridable — no code list
  // is exhaustive, so the toggle makes "any language" honest for any script.
  // null = follow the code; once set, the operator's choice sticks.
  const [customOn, setCustomOn] = useState(false);
  const [customName, setCustomName] = useState("");
  const [customCode, setCustomCode] = useState("");
  const [customDir, setCustomDir] = useState<"rtl" | "ltr" | null>(null);
  const effCustomDir: "rtl" | "ltr" = customDir ?? directionForCode(customCode);
  // Memoized: makeCustomLanguage builds a fresh object per call, and effLang
  // feeds the Design step's inputProps memo — an unstable identity would
  // rebuild the studio baseline on every wizard render (Player churn).
  const effLang: LanguagePreset = useMemo(
    () => (customOn ? makeCustomLanguage(customName, customCode, effCustomDir) : language),
    [customOn, customName, customCode, effCustomDir, language],
  );
  const customCodeValid = /^[a-z]{2,3}$/i.test(customCode.trim());
  const isArabicChannel = effLang.code === "ar";
  // colors
  const [accent, setAccent] = useState("#FFD500");
  const [captionColor, setCaptionColor] = useState(DEFAULTS.captionColor);
  const [captionAccent, setCaptionAccent] = useState(DEFAULTS.captionAccent);
  const [speakerNameColor, setSpeakerNameColor] = useState(DEFAULTS.speakerNameColor);
  const [watermark, setWatermark] = useState("");
  // logo
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);
  const [logoFilename, setLogoFilename] = useState<string | null>(null);
  const [logoWidthPct, setLogoWidthPct] = useState(DEFAULTS.logoWidthPct);
  const [logoBottomPx, setLogoBottomPx] = useState(DEFAULTS.logoBottomPx);
  const [logoOpacity, setLogoOpacity] = useState(1);
  // Horizontal centre of the logo (% of canvas width). 50 = founding centre.
  const [logoXPct, setLogoXPct] = useState(DEFAULTS.logoXPct);
  // font
  const [fontDataUrl, setFontDataUrl] = useState<string | null>(null);
  const [fontFilename, setFontFilename] = useState<string | null>(null);
  const [fontFamily, setFontFamily] = useState("");
  // Per-element font FAMILY assignment ("" = auto → uploaded font or founding
  // default). Set from the canvas panels; persisted via payload fontRoles.
  const [fontRoles, setFontRoles] = useState({ caption: "", punch: "", speaker: "" });
  // Registered font files for the canvas preview (family → object URL), fetched
  // from /api/fonts/:family. The fresh upload previews via its data URL instead.
  const [fontUrls, setFontUrls] = useState<Record<string, string>>({});
  const requestedFonts = useRef(new Set<string>());
  // Curated out-of-the-box font library (founding trio + open-licensed set),
  // listed by the server so the gallery renders every face before any upload.
  const [libraryFamilies, setLibraryFamilies] = useState<FontFamilyEntry[]>([]);
  useEffect(() => { void listFontFamiliesApi().then(setLibraryFamilies); }, []);
  // The Design step's collapsed layout/brand journal — overlays the wizard's
  // base values in every payload (the operator's studio edits always win).
  const [designPatch, setDesignPatch] = useState<EditPatch | null>(null);
  const [designSteps, setDesignSteps] = useState(0);
  const handleDesignChange = useCallback((design: EditPatch, steps: number): void => {
    setDesignPatch(design);
    setDesignSteps(steps);
  }, []);
  // Edit mode: the channel's FULL saved caption layout (incl. styling-v2
  // fields the classic wizard state doesn't model — stroke, wrapMode, align,
  // opacity, twoLine gap…). Base for the Design step + payload so a wizard
  // Edit → Save can never silently strip a promoted design (critic-caught).
  const [savedLayout, setSavedLayout] = useState<NonNullable<CreateChannelInput["layout"]> | null>(null);
  // captions
  const [capBottomPx, setCapBottomPx] = useState(DEFAULTS.capBottomPx);
  const [capFontSizePx, setCapFontSizePx] = useState(DEFAULTS.capFontSizePx);
  // Two-line mode: top and punch lines sized INDEPENDENTLY (no derived multiplier).
  const [capTopPx, setCapTopPx] = useState(DEFAULTS.capTopPx);
  const [capPunchPx, setCapPunchPx] = useState(DEFAULTS.capPunchPx);
  const [capUniform, setCapUniform] = useState(DEFAULTS.capUniform);
  const [capBox, setCapBox] = useState(false);
  const [capBoxOpacity, setCapBoxOpacity] = useState(0.25);
  const [capFlatText, setCapFlatText] = useState(false);
  const [capSidePaddingPx, setCapSidePaddingPx] = useState(160);
  const [capLineHeight, setCapLineHeight] = useState(1.12);
  // Caption letter-spacing in px (negative = tighter). Founding default 0.5.
  const [capLetterSpacingPx, setCapLetterSpacingPx] = useState(0.5);
  // Speaker name card placement (% of 2160×3840, top-left anchor). Founding
  // coords = 130px / 320px → the render's default when unset.
  const [speakerCardXPct, setSpeakerCardXPct] = useState(DEFAULTS.speakerCardXPct);
  const [speakerCardYPct, setSpeakerCardYPct] = useState(DEFAULTS.speakerCardYPct);
  // Size multiplier over the founding 35%-width card (1 = founding size).
  const [speakerCardScale, setSpeakerCardScale] = useState(1);
  // pipelines / voice — the video toolkit (captions + auto-scan + find-a-scene)
  // ships with every channel; Posts/Originals are locked ("coming soon") until
  // they get their own onboarding. The set is fixed here, so there's no setter.
  const [features] = useState({ videos: true, posts: false, originals: false, autoScan: true });
  const [voicePromptFragment, setVoicePromptFragment] = useState("");
  // Fixed sign-off appended to every video caption (optional; empty = none).
  const [socialCta, setSocialCta] = useState("");
  // reference (optional target-look screenshot + notes)
  const [refDataUrl, setRefDataUrl] = useState<string | null>(null);
  const [refFilename, setRefFilename] = useState<string | null>(null);
  const [refNotes, setRefNotes] = useState("");
  // reference-vision analysis (Part B) — advisory pre-fill of colors/layout/font.
  const [analyzing, setAnalyzing] = useState(false);
  const [refProfile, setRefProfile] = useState<StylingProfile | null>(null);
  // The exact dataUrl already analyzed — so re-entering the step doesn't re-run
  // (and clobber operator edits); a NEW upload does.
  const [analyzedDataUrl, setAnalyzedDataUrl] = useState<string | null>(null);
  // True once we tried and the reference couldn't be read (advisory note only).
  const [refAnalysisFailed, setRefAnalysisFailed] = useState(false);
  // Fields pre-filled from the reference — each resettable in a "Matched" panel.
  const [matched, setMatched] = useState<MatchedField[]>([]);
  // Font-feel suggestion (informational; never auto-applied).
  const [fontSuggestion, setFontSuggestion] = useState<string | null>(null);
  // buffer (optional)
  const [bufferToken, setBufferToken] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  // Set once the channel exists so a Buffer failure doesn't force a re-create.
  const [createdSlug, setCreatedSlug] = useState<string | null>(null);

  // Edit mode: load the saved channel once and pre-fill every field so the
  // operator can review + change what they set. Slug is locked (immutable);
  // logo/font are kept unless re-uploaded (the wizard can't rebuild a data URL).
  useEffect(() => {
    if (!editSlug) return;
    let cancelled = false;
    setEditLoading(true);
    void (async () => {
      try {
        const ch = await getChannelApi(editSlug);
        if (cancelled) return;
        setDisplayName(ch.displayName);
        setSlug(ch.slug);
        setSlugTouched(true);
        const lang = ch.language;
        if (lang) {
          const preset = LANGUAGE_PRESETS.find((p) => p.code === lang.code);
          if (preset) { setCustomOn(false); setLanguage(preset); }
          else { setCustomOn(true); setCustomName(lang.displayName); setCustomCode(lang.code); setCustomDir(lang.direction); }
        } else { setCustomOn(false); setLanguage(DEFAULT_LANGUAGE); }
        setAccent(ch.brand.accent);
        setCaptionColor(ch.brand.captionColor);
        setCaptionAccent(ch.brand.captionAccent);
        setSpeakerNameColor(ch.brand.speakerNameColor);
        setWatermark(ch.brand.watermark ?? "");
        setLogoWidthPct(ch.layout.logo.widthPct);
        setLogoBottomPx(ch.layout.logo.bottomPx);
        setLogoOpacity(ch.layout.logo.opacity);
        setLogoXPct(ch.layout.logo.xPct ?? DEFAULTS.logoXPct);
        const fam = ch.brand.fonts?.caption;
        if (fam && fam !== "AlHurra") { setExistingFontFamily(fam); setFontFamily(fam); }
        setSavedLayout(ch.layout as NonNullable<CreateChannelInput["layout"]>);
        const cap = ch.layout.caption;
        setCapBottomPx(cap.bottomPx);
        setCapFontSizePx(cap.fontSizePx);
        setCapUniform(cap.twoLine === null);
        if (cap.twoLine) { setCapTopPx(cap.twoLine.topPx); setCapPunchPx(cap.twoLine.bottomPx); }
        if (ch.brand.fonts) {
          setFontRoles({
            caption: ch.brand.fonts.caption ?? "",
            punch: ch.brand.fonts.punch ?? "",
            speaker: ch.brand.fonts.speaker ?? "",
          });
        }
        setCapBox(!!cap.box);
        setCapBoxOpacity(cap.box?.opacity ?? 0.25);
        setCapFlatText(cap.textShadow === "none");
        setCapSidePaddingPx(cap.sidePaddingPx);
        setCapLineHeight(cap.lineHeight);
        setCapLetterSpacingPx(parseFloat(cap.letterSpacing ?? "0.5") || 0.5);
        setSpeakerCardXPct(ch.layout.speakerCard?.xPct ?? DEFAULTS.speakerCardXPct);
        setSpeakerCardYPct(ch.layout.speakerCard?.yPct ?? DEFAULTS.speakerCardYPct);
        setSpeakerCardScale(ch.layout.speakerCard?.scale ?? 1);
        setVoicePromptFragment(ch.voicePromptFragment ?? "");
        setSocialCta(ch.socialCta ?? "");
        // The real logo (auth-safe object URL) — shown as-is, kept unless replaced.
        const logoUrl = await fetchChannelLogoObjectUrl(ch.slug);
        if (!cancelled && logoUrl) { setLogoDataUrl(logoUrl); setLogoFilename("current logo"); }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setEditLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editSlug]);

  const effectiveSlug = slugTouched && slug ? slug : slugify(displayName);
  const hex = (v: string): boolean => /^#[0-9a-fA-F]{6}$/.test(v);

  const canAdvance = ((): boolean => {
    switch (stepId) {
      case "basics": return effectiveSlug.length >= 2 && displayName.trim().length > 0;
      case "language": return !customOn || (customName.trim().length > 0 && customCodeValid);
      case "colors": return [accent, captionColor, captionAccent, speakerNameColor].every(hex);
      case "font": return true;
      case "layout": return !!logoDataUrl;
      case "features": return true; // the video toolkit is always included — nothing to choose
      default: return true;
    }
  })();

  async function handleLogoFile(file: File | null | undefined): Promise<void> {
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/.test(file.type)) return setError("Logo must be PNG, JPG, or WebP");
    if (file.size > 4 * 1024 * 1024) return setError("Logo too large (max 4 MiB)");
    setError(null);
    setLogoDataUrl(await fileToDataUrl(file));
    setLogoFilename(file.name);
  }

  async function handleFontFile(file: File | null | undefined): Promise<void> {
    if (!file) return;
    if (!/\.(ttf|otf)$/i.test(file.name)) return setError("Font must be a .ttf or .otf file");
    if (file.size > 6 * 1024 * 1024) return setError("Font too large (max 6 MiB)");
    setError(null);
    setFontDataUrl(await fileToDataUrl(file));
    setFontFilename(file.name);
    if (!fontFamily) setFontFamily(file.name.replace(/\.(ttf|otf)$/i, "").replace(/[^A-Za-z0-9_-]/g, ""));
  }

  async function handleReferenceFile(file: File | null | undefined): Promise<void> {
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/.test(file.type)) return setError("Reference must be PNG, JPG, or WebP");
    if (file.size > 8 * 1024 * 1024) return setError("Reference too large (max 8 MiB)");
    setError(null);
    const url = await fileToDataUrl(file);
    setRefDataUrl(url);
    setRefFilename(file.name);
    // A different image invalidates any prior read (re-analyze on next "Next").
    if (url !== analyzedDataUrl) setRefAnalysisFailed(false);
  }

  /**
   * Map a styling profile onto the wizard's colors / layout / font-suggestion
   * state and record each applied field so the operator sees (and can revert)
   * exactly what the reference changed. ADVISORY — never touches a field the
   * profile didn't speak to; colors/layout steps all sit after Reference, so at
   * apply-time they're still at defaults (no operator edits to clobber).
   */
  function applyProfile(p: StylingProfile): void {
    const m: MatchedField[] = [];
    const cap = p.caption;
    if (cap) {
      const body = normHex(cap.bodyColorHex);
      if (body) {
        setCaptionColor(body);
        m.push({ key: "captionColor", scope: "colors", label: "Caption text", detected: body, swatch: body, revert: () => setCaptionColor(DEFAULTS.captionColor) });
      }
      const punch = normHex(cap.punchColorHex);
      if (punch) {
        setCaptionAccent(punch);
        setSpeakerNameColor(punch);
        m.push({ key: "captionAccent", scope: "colors", label: "Caption highlight", detected: punch, swatch: punch, revert: () => setCaptionAccent(DEFAULTS.captionAccent) });
        m.push({ key: "speakerNameColor", scope: "colors", label: "Speaker name", detected: punch, swatch: punch, revert: () => setSpeakerNameColor(DEFAULTS.speakerNameColor) });
      }
      if (cap.style) {
        const uniform = cap.style === "one-line";
        setCapUniform(uniform);
        m.push({ key: "capUniform", scope: "captions", label: "Caption style", detected: uniform ? "single-size lines" : "two-line big punch", revert: () => setCapUniform(DEFAULTS.capUniform) });
      }
      if (cap.approxFontScale) {
        const px = fontScaleToPx(cap.approxFontScale);
        setCapFontSizePx(px);
        // Seed the two-line sizes proportionally from the matched size so a
        // reference-derived channel keeps a sensible punch; still independently editable.
        setCapTopPx(Math.round(px * 0.95)); setCapPunchPx(Math.round(px * 1.9));
        m.push({ key: "capFontSizePx", scope: "captions", label: "Caption size", detected: `${cap.approxFontScale} · ${px}px`, revert: () => setCapFontSizePx(DEFAULTS.capFontSizePx) });
      }
      if (cap.placement) {
        const bottomPx = captionPlacementToBottomPx(cap.placement);
        setCapBottomPx(bottomPx);
        m.push({ key: "capBottomPx", scope: "captions", label: "Caption position", detected: cap.placement, revert: () => setCapBottomPx(DEFAULTS.capBottomPx) });
      }
    }
    if (p.logo && p.logo.placement && p.logo.placement !== "none") {
      const { xPct, bottomPx } = logoCornerToPos(p.logo.placement);
      setLogoXPct(xPct);
      setLogoBottomPx(bottomPx);
      m.push({ key: "logoPos", scope: "logo", label: "Logo position", detected: p.logo.placement, revert: () => { setLogoXPct(DEFAULTS.logoXPct); setLogoBottomPx(DEFAULTS.logoBottomPx); } });
    }
    if (p.logo?.approxSize) {
      const w = logoSizeToWidthPct(p.logo.approxSize);
      setLogoWidthPct(w);
      m.push({ key: "logoWidthPct", scope: "logo", label: "Logo size", detected: `${p.logo.approxSize} · ${w}%`, revert: () => setLogoWidthPct(DEFAULTS.logoWidthPct) });
    }
    if (p.speakerCard?.present && p.speakerCard.placement && p.speakerCard.placement !== "none") {
      const { xPct, yPct } = speakerCornerToPos(p.speakerCard.placement);
      setSpeakerCardXPct(xPct);
      setSpeakerCardYPct(yPct);
      m.push({ key: "speakerCard", scope: "speaker", label: "Speaker card", detected: p.speakerCard.placement, revert: () => { setSpeakerCardXPct(DEFAULTS.speakerCardXPct); setSpeakerCardYPct(DEFAULTS.speakerCardYPct); } });
    }
    // Mood seeds the brand-voice fragment ONLY when the operator hasn't typed one.
    if (p.mood && !voicePromptFragment.trim()) {
      const mood = p.mood;
      setVoicePromptFragment(mood);
      m.push({ key: "mood", scope: "voice", label: "Brand voice seed", detected: mood, revert: () => setVoicePromptFragment("") });
    }
    setMatched(m);
    setFontSuggestion(p.font ? (describeFontSuggestion(p.font) || null) : null);
  }

  /** Read the reference (once per image) and pre-fill from its styling. */
  async function runReferenceAnalysis(): Promise<void> {
    if (!refDataUrl || refDataUrl === analyzedDataUrl) return;
    setAnalyzing(true);
    setError(null);
    try {
      const profile = await analyzeReferenceApi(refDataUrl);
      setAnalyzedDataUrl(refDataUrl);
      if (!profile) {
        setRefProfile(null);
        setMatched([]);
        setFontSuggestion(null);
        setRefAnalysisFailed(true);
        return;
      }
      setRefProfile(profile);
      setRefAnalysisFailed(false);
      applyProfile(profile);
    } finally {
      setAnalyzing(false);
    }
  }

  /** Revert one matched field to its founding default and drop its chip. */
  const resetMatchedField = (key: string): void => {
    setMatched((prev) => {
      const f = prev.find((x) => x.key === key);
      f?.revert();
      return prev.filter((x) => x.key !== key);
    });
  };

  /** Revert every matched field within the given scopes (a per-step "Reset"). */
  const resetMatchedScopes = (scopes: MatchScope[]): void => {
    setMatched((prev) => {
      prev.filter((x) => scopes.includes(x.scope)).forEach((x) => x.revert());
      return prev.filter((x) => !scopes.includes(x.scope));
    });
  };

  /** Shared props for the composed preview — the same values that drive the render. */
  const previewProps = {
    captionColor, captionAccent, speakerNameColor, watermark: watermark.trim() || undefined,
    logoDataUrl, logoWidthPct, logoBottomPx, logoOpacity, logoXPct,
    speakerCard: { xPct: speakerCardXPct, yPct: speakerCardYPct, scale: speakerCardScale },
    capBottomPx, capFontSizePx, capTopPx, capPunchPx, capUniform, capBox, capBoxOpacity, capFlat: capFlatText,
    capSidePaddingPx, capLineHeight,
    fontFamily: fontFamily || undefined, fontDataUrl,
    capFontFamily: fontRoles.caption || undefined,
    punchFontFamily: fontRoles.punch || undefined,
    speakerFontFamily: fontRoles.speaker || undefined,
    direction: effLang.direction,
    sampleTop: effLang.sample.top,
    samplePunch: effLang.sample.punch,
    sampleSpeaker: effLang.sampleSpeaker,
  };

  // Fetch every font family the canvas may need to preview (founding trio +
  // the channel's existing families + any picked role) exactly once each.
  useEffect(() => {
    const want = [...FOUNDING_FAMILIES, ...libraryFamilies.map((f) => f.family), fontRoles.caption, fontRoles.punch, fontRoles.speaker, existingFontFamily ?? ""]
      .filter((f): f is string => !!f);
    for (const fam of want) {
      if (requestedFonts.current.has(fam)) continue;
      requestedFonts.current.add(fam);
      void fetchFontObjectUrl(fam).then((url) => {
        if (url) setFontUrls((m) => ({ ...m, [fam]: url }));
      });
    }
  }, [fontRoles.caption, fontRoles.punch, fontRoles.speaker, existingFontFamily, libraryFamilies]);

  /** @font-face for every fetched family + the fresh upload, injected once so
   *  the canvas, panels, and preview all render the real faces. */
  const fontFaceCss = [
    ...Object.entries(fontUrls).map(([fam, url]) => `@font-face { font-family: "${fam}"; src: url(${url}); font-display: swap; }`),
    ...(fontDataUrl && fontFamily ? [`@font-face { font-family: "${fontFamily}"; src: url(${fontDataUrl}); font-display: swap; }`] : []),
  ].join("\n");

  /** The "Matched from your reference" panel for a step's field scopes.
   *  Renders null when nothing in scope was matched (and no note). */
  const matchedPanel = (scopes: MatchScope[], note?: string | null): React.JSX.Element => (
    <MatchedPanel
      scopes={scopes}
      matched={matched}
      note={note ?? null}
      confidence={refProfile?.overallConfidence ?? null}
      accent={accent}
      onResetOne={resetMatchedField}
      onResetScopes={resetMatchedScopes}
    />
  );

  /** The wizard's BASE layout (pre-Design-step): defaults + reference-vision
   *  pre-fill + edit-mode prefill. `justify: "flex-end"` pins the caption to
   *  bottomPx so the render matches the preview (Quotologies keeps "center"
   *  via its own channel.json). */
  const buildBaseLayout = (): NonNullable<CreateChannelInput["layout"]> => {
    // EDIT mode: the saved channel layout IS the base — it carries every
    // field verbatim (incl. styling v2 the classic state below can't
    // express), so Save without touching the Design step round-trips
    // byte-identically. The wizard no longer edits caption fields outside
    // the Design studio, so the classic state can't diverge from it.
    if (isEdit && savedLayout) return savedLayout;
    return {
    caption: {
      captionStyle: capUniform ? "uniform" : "punch",
      bottomPx: capBottomPx,
      fontSizePx: capFontSizePx,
      twoLine: capUniform ? null : { topPx: capTopPx, bottomPx: capPunchPx },
      box: capBox ? { color: "#000000", opacity: capBoxOpacity, radiusPx: 14, padXPx: 26, padYPx: 10 } : null,
      textShadow: capFlatText ? "none" : "0 4px 12px rgba(0,0,0,0.85), 0 2px 4px rgba(0,0,0,0.85)",
      sidePaddingPx: capSidePaddingPx,
      lineHeight: capLineHeight,
      letterSpacing: `${capLetterSpacingPx}px`,
      justify: "flex-end",
    },
    logo: { widthPct: logoWidthPct, bottomPx: logoBottomPx, opacity: logoOpacity, xPct: logoXPct },
    speakerArrow: null,
    speakerCard: { xPct: speakerCardXPct, yPct: speakerCardYPct, ...(speakerCardScale !== 1 ? { scale: speakerCardScale } : {}) },
    };
  };

  /** Final layout payload: base + the Design studio's edits (field-level, the
   *  studio always wins — it's the surface the operator actually saw). */
  const buildLayoutPayload = (): NonNullable<CreateChannelInput["layout"]> => {
    const base = buildBaseLayout();
    const d = designPatch?.layout;
    if (!d) return base;
    // Spreading a Partial widens props to `T | undefined` in TS even though
    // absent keys contribute nothing at runtime — cast back to the full shape.
    // withoutUnset: a `null` in the design is the UNSET signal (see the UNSET
    // CONVENTION in src/lib/clipEditState.ts) — it REMOVES the key, so the new
    // channel is created without it rather than carrying a literal null that
    // parseLayout would reject anyway.
    return {
      caption: withoutUnset(
        { ...base.caption, ...d.caption },
        d.caption,
        CAPTION_UNSETTABLE,
      ) as NonNullable<CreateChannelInput["layout"]>["caption"],
      logo: { ...base.logo, ...d.logo } as NonNullable<CreateChannelInput["layout"]>["logo"],
      speakerArrow: base.speakerArrow,
      speakerCard: withoutUnset(
        {
          xPct: base.speakerCard?.xPct ?? 6.02,
          yPct: base.speakerCard?.yPct ?? 8.33,
          ...base.speakerCard,
          ...d.speakerCard,
        },
        d.speakerCard,
        SPEAKER_CARD_UNSETTABLE,
      ) as NonNullable<NonNullable<CreateChannelInput["layout"]>["speakerCard"]>,
      // video: absent stays absent — only carried when the design (or the
      // saved channel, in edit mode) set SOMETHING. Field-level, not
      // dim-only: a design that touched brightness or contrast but not dim
      // used to be dropped on Save, so the wizard silently threw away a
      // lighting change the operator had just watched happen.
      ...((): { video?: NonNullable<CreateChannelInput["layout"]>["video"] } => {
        const video = { ...base.video, ...d.video };
        return Object.keys(video).length ? { video } : {};
      })(),
      // overlays: the Design studio owns the WHOLE array, so the patch wins
      // whenever it mentions them — including `[]`, which is a CLEAR (same rule
      // as applyEditState). Silent patch → whatever the base carries (in edit
      // mode that IS the saved channel's CTA). Rebuilding the layout from an
      // explicit key list used to drop the key entirely, so Edit pencil → touch
      // Studio → Save deleted the brand's CTA.
      ...((): { overlays?: NonNullable<CreateChannelInput["layout"]>["overlays"] } => {
        const ov = d.overlays ?? base.overlays;
        return ov && ov.length ? { overlays: ov } : {};
      })(),
    };
  };

  /** Baseline props for the Design step's live Studio: the wizard's collected
   *  brand + base layout over the committed sample clip. The studio's journal
   *  replays ON TOP of this — so the memo must NOT depend on designPatch, or
   *  every design commit would rebuild the baseline under the journal. */
  const sampleInputProps = useMemo(() => {
    const uploadFamily = fontDataUrl ? (fontFamily || `${effectiveSlug}-font`) : "";
    const fonts = {
      caption: fontRoles.caption || uploadFamily || "AlHurra",
      punch: fontRoles.punch || uploadFamily || "Lalezar",
      speaker: fontRoles.speaker || uploadFamily || "Faseh",
    };
    const fontFaces: FontFaceSpec[] = [
      ...libraryFamilies.map((f) => ({
        family: f.family,
        weight: f.weight ?? 700,
        src: `/api/fonts/${encodeURIComponent(f.family)}`,
      })),
      ...(fontDataUrl && uploadFamily
        ? [
            { family: uploadFamily, weight: 400, src: fontDataUrl },
            { family: uploadFamily, weight: 700, src: fontDataUrl },
          ]
        : []),
    ];
    const base = buildBaseLayout();
    // buildBaseLayout always emits every caption field; the API input type
    // just declares them optional (older clients could omit).
    const layout: RenderLayout = {
      caption: base.caption as RenderLayout["caption"],
      logo: base.logo!,
      ...(base.speakerCard ? { speakerCard: base.speakerCard } : {}),
      // Edit mode: the saved channel may carry a custom dim — dropping it
      // here would preview (and let the operator re-baseline) at 0.28 while
      // Save round-trips the real value (critic-caught).
      ...(base.video ? { video: base.video } : {}),
    };
    const props = buildSampleInputProps({
      direction: effLang.direction,
      glyphRatio: effLang.glyphRatio,
      captionColor,
      captionAccent,
      fonts,
      fontFaces,
      // No upload yet → the web-served placeholder. Omitting it entirely
      // would make LogoMark fall back to the render bundle's legacy path,
      // which the web app doesn't serve (endless image-retry warnings).
      logoSrc: logoDataUrl ?? "/logo-quotologies.png",
    });
    return { ...props, layout };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    effLang, captionColor, captionAccent, fontRoles, fontDataUrl, fontFamily, effectiveSlug,
    libraryFamilies, logoDataUrl, capBottomPx, capFontSizePx, capTopPx, capPunchPx, capUniform,
    capBox, capBoxOpacity, capFlatText, capSidePaddingPx, capLineHeight, capLetterSpacingPx,
    logoWidthPct, logoBottomPx, logoOpacity, logoXPct, speakerCardXPct, speakerCardYPct, speakerCardScale,
  ]);

  /** Arabic → no language field (founding pipeline byte-identical); else persist it. */
  const buildLanguagePayload = (): CreateChannelInput["language"] =>
    effLang.code === "ar"
      ? undefined
      : {
          code: effLang.code,
          displayName: effLang.displayName,
          direction: effLang.direction,
          register: effLang.register,
          budgets: effLang.budgets,
          glyphRatio: effLang.glyphRatio,
        };

  const buildReferencePayload = (): CreateChannelInput["reference"] =>
    (refDataUrl || refNotes.trim())
      ? { dataUrl: refDataUrl ?? undefined, notes: refNotes.trim() || undefined }
      : undefined;

  /** Per-element font families for the payload — omitted entirely when every
   *  role is auto, so the server keeps its historical single-font behaviour.
   *  A font picked in the Design studio (designPatch.brand.*Font) wins over
   *  the Font-step gallery pick. */
  const buildFontRolesPayload = (): CreateChannelInput["fontRoles"] => {
    const d = designPatch?.brand;
    const caption = d?.captionFont || fontRoles.caption;
    const punch = d?.punchFont || fontRoles.punch;
    const speaker = d?.speakerFont || fontRoles.speaker;
    return (caption || punch || speaker)
      ? {
          caption: caption || undefined,
          punch: punch || undefined,
          speaker: speaker || undefined,
        }
      : undefined;
  };

  /** Brand colors with the Design studio's recolors applied (studio wins).
   *  speakerNameColor tracks a studio accent recolor when the wizard had
   *  them in lockstep (the reference-vision pre-fill sets them together, and
   *  the sample preview paints the accent everywhere the speaker name will
   *  appear) — an explicit divergent wizard choice is respected. */
  const effBrandColors = (): { captionColor: string; captionAccent: string; speakerNameColor: string } => {
    const accentOverride = designPatch?.brand?.captionAccent;
    return {
      captionColor: designPatch?.brand?.captionColor ?? captionColor,
      captionAccent: accentOverride ?? captionAccent,
      speakerNameColor:
        accentOverride && speakerNameColor === captionAccent ? accentOverride : speakerNameColor,
    };
  };

  async function submit(): Promise<void> {
    if (!logoDataUrl) { setStepIdx(STEPS.findIndex((s) => s.id === "layout")); return setError("Logo is required"); }
    setSubmitting(true);
    setError(null);

    // EDIT: update the existing channel in place. A prefilled logo is an object
    // URL (blob:) and a prefilled font has no data URL — only a genuine
    // RE-upload (a data: URL) is sent, so untouched assets are kept server-side.
    if (isEdit && editSlug) {
      const newLogo = logoDataUrl.startsWith("data:") ? logoDataUrl : undefined;
      const editInput: EditChannelInput = {
        displayName: displayName.trim(),
        brand: {
          accent, ...effBrandColors(),
          watermark: watermark.trim() || undefined,
          ...(newLogo ? { logoDataUrl: newLogo } : {}),
        },
        font: fontDataUrl ? { dataUrl: fontDataUrl, family: fontFamily || `${effectiveSlug}-font` } : undefined,
        fontRoles: buildFontRolesPayload(),
        layout: buildLayoutPayload(),
        features,
        voicePromptFragment: voicePromptFragment.trim() || undefined,
        socialCta: socialCta.trim() || undefined,
        reference: buildReferencePayload(),
        language: buildLanguagePayload(),
      };
      try {
        await updateChannelFullApi(editSlug, editInput);
        clearOnboardingDesignDraft(editSlug);
        onDone(editSlug);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        setSubmitting(false);
      }
      return;
    }

    const input: CreateChannelInput = {
      slug: effectiveSlug,
      displayName: displayName.trim(),
      brand: {
        accent,
        ...effBrandColors(),
        watermark: watermark.trim() || undefined,
        logoDataUrl,
      },
      font: fontDataUrl ? { dataUrl: fontDataUrl, family: fontFamily || `${effectiveSlug}-font` } : undefined,
      fontRoles: buildFontRolesPayload(),
      layout: buildLayoutPayload(),
      features,
      voicePromptFragment: voicePromptFragment.trim() || undefined,
      socialCta: socialCta.trim() || undefined,
      reference: buildReferencePayload(),
      language: buildLanguagePayload(),
    };
    try {
      // Create + activate once; a retry after a Buffer failure reuses the slug
      // rather than re-creating (which would fail "already exists").
      let slugLocal = createdSlug;
      if (!slugLocal) {
        const created = await createChannelApi(input);
        await activateChannelApi(created.slug);
        slugLocal = created.slug;
        setCreatedSlug(created.slug);
      }
      const token = bufferToken.trim();
      if (token) {
        try {
          await connectChannelScheduling(slugLocal, token);
        } catch (bufErr) {
          // The channel exists — a Buffer hiccup must not lose the brand setup.
          setWarning(
            `Channel created & activated, but Buffer didn't connect: ${bufErr instanceof Error ? bufErr.message : String(bufErr)}. ` +
            `You can connect it later in Settings → Scheduling.`,
          );
          setSubmitting(false);
          return; // stay so the operator sees the warning; the Finish button proceeds
        }
      }
      clearOnboardingDesignDraft("new");
      onDone(slugLocal);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSubmitting(false);
    }
  }

  const next = (): void => setStepIdx((i) => Math.min(STEPS.length - 1, i + 1));
  const back = (): void => setStepIdx((i) => Math.max(0, i - 1));

  /** Advance — but when leaving Reference with a fresh image, read its styling
   *  first (spinner on the button) so the next steps land pre-filled. */
  const handleNext = async (): Promise<void> => {
    if (stepId === "reference" && refDataUrl && refDataUrl !== analyzedDataUrl) {
      await runReferenceAnalysis();
    }
    next();
  };

  return (
    <div>
      {fontFaceCss && <style>{fontFaceCss}</style>}
      <PageHeader
        title={isEdit ? `Edit channel${displayName ? ` — ${displayName}` : ""}` : "Onboard a new channel"}
        subtitle={isEdit
          ? "Review and change anything you set — colors, layout, captions, font, voice. Save re-writes the channel; re-render a clip to see it applied."
          : "Reproduce your brand's video system — colors, font, logo, caption layout."}
      />
      {editLoading && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, fontSize: 13, color: "var(--ql-fg-muted)" }}>
          <Loader2 size={14} className="ql-spin" /> Loading this channel's saved settings…
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginBottom: 28, flexWrap: "wrap" }}>
        {STEPS.map((s, i) => {
          const done = i < stepIdx;
          const current = i === stepIdx;
          return (
            <button key={s.id} onClick={() => setStepIdx(i)} style={{
              display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 999,
              border: "1px solid " + (current ? accent : "rgba(255,255,255,.08)"),
              background: current ? "rgba(255,213,0,.08)" : "rgba(255,255,255,.02)",
              color: current ? "var(--ql-fg)" : done ? "var(--ql-fg-muted)" : "var(--ql-fg-subtle)",
              fontSize: 12, fontFamily: "inherit", cursor: "pointer",
            }}>
              {done && <Check size={12} />}<span>{i + 1}. {s.label}</span>
            </button>
          );
        })}
      </div>

      <div style={cardStyle}>
        {stepId === "basics" && (
          <div style={{ display: "grid", gap: 18 }}>
            <Field label="Channel display name">
              <input style={inputStyle} value={displayName} placeholder="e.g. Quotologies Business"
                onChange={(e) => { setDisplayName(e.target.value); if (!slugTouched) setSlug(slugify(e.target.value)); }} />
            </Field>
            <Field label="URL slug" hint={isEdit ? "The slug can't change after a channel is created — it's baked into file paths + credentials." : "Used in file paths + the Buffer credentials slot. Lowercase letters, digits, dashes."}>
              <input style={{ ...inputStyle, opacity: isEdit ? 0.6 : 1, cursor: isEdit ? "not-allowed" : "text" }} value={effectiveSlug} placeholder="quotologies-business" disabled={isEdit}
                onChange={(e) => { setSlugTouched(true); setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "")); }} />
            </Field>
          </div>
        )}

        {stepId === "language" && (
          <div style={{ display: "grid", gap: 18 }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>What language should the captions be in?</div>
              <div style={{ fontSize: 13, color: "var(--ql-fg-muted)", lineHeight: 1.6 }}>
                Your video's spoken language is <strong>detected automatically</strong> — you don't pick it here. This
                is the language your <strong>captions</strong> come out in. When it matches the audio we transcribe;
                when it differs we translate. It also sets the caption direction, the speaker-card bio, and the social
                caption.
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: 10 }}>
              {LANGUAGE_PRESETS.map((lp) => {
                const on = !customOn && lp.code === language.code;
                return (
                  <button key={lp.code} type="button"
                    onClick={() => { setCustomOn(false); setLanguage(lp); }}
                    style={{
                      display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4,
                      padding: "12px 14px", borderRadius: 12, cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                      border: "1px solid " + (on ? accent : "rgba(255,255,255,.08)"),
                      background: on ? "rgba(255,213,0,.07)" : "rgba(255,255,255,.02)",
                      color: on ? "var(--ql-fg)" : "var(--ql-fg-muted)",
                    }}>
                    <span style={{ fontSize: 14, fontWeight: 700 }}>{lp.displayName}</span>
                    <span style={{ fontSize: 11, color: "var(--ql-fg-subtle)", textTransform: "uppercase", letterSpacing: ".05em" }}>
                      {lp.code} · {lp.direction === "rtl" ? "right-to-left" : "left-to-right"}
                    </span>
                  </button>
                );
              })}
              {/* "Other" — any language not in the curated list. */}
              <button type="button" onClick={() => setCustomOn(true)} style={{
                display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4,
                padding: "12px 14px", borderRadius: 12, cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                border: "1px dashed " + (customOn ? accent : "rgba(255,255,255,.16)"),
                background: customOn ? "rgba(255,213,0,.07)" : "rgba(255,255,255,.02)",
                color: customOn ? "var(--ql-fg)" : "var(--ql-fg-muted)",
              }}>
                <span style={{ fontSize: 14, fontWeight: 700 }}>Other…</span>
                <span style={{ fontSize: 11, color: "var(--ql-fg-subtle)", textTransform: "uppercase", letterSpacing: ".05em" }}>
                  any language
                </span>
              </button>
            </div>

            {customOn && (
              <div style={{
                display: "grid", gap: 16,
                padding: 16, borderRadius: 14, border: "1px solid rgba(255,255,255,.08)", background: "rgba(255,255,255,.02)",
              }}>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 160px", gap: 14 }}>
                  <Field label="Language name" hint="As you'd write it in the caption's own tongue — shown in the review + saved with the channel.">
                    <input style={inputStyle} value={customName} placeholder="e.g. Polish" onChange={(e) => setCustomName(e.target.value)} />
                  </Field>
                  <Field label="Code (ISO 639-1)" hint="2-letter, e.g. pl, ja, hi">
                    <input style={{ ...inputStyle, fontFamily: "var(--ql-font-mono)" }} value={customCode} maxLength={3}
                      placeholder="pl" onChange={(e) => { setCustomCode(e.target.value.toLowerCase().replace(/[^a-z]/g, "")); }} />
                  </Field>
                </div>
                <Field
                  label="Caption direction"
                  hint={customDir === null
                    ? "Seeded from the code — override if this language's script reads the other way."
                    : "Set by you — this is what gets saved with the channel."}
                >
                  <div style={{ display: "flex", gap: 8 }}>
                    {([["ltr", "Left-to-right"], ["rtl", "Right-to-left"]] as const).map(([dir, lbl]) => {
                      const on = effCustomDir === dir;
                      return (
                        <button key={dir} type="button" onClick={() => setCustomDir(dir)} style={{
                          flex: 1, padding: "10px 12px", borderRadius: 10, cursor: "pointer", fontFamily: "inherit",
                          fontSize: 13, fontWeight: 600,
                          border: "1px solid " + (on ? accent : "rgba(255,255,255,.10)"),
                          background: on ? "rgba(255,213,0,.07)" : "rgba(255,255,255,.02)",
                          color: on ? "var(--ql-fg)" : "var(--ql-fg-muted)",
                        }}>{lbl}</button>
                      );
                    })}
                  </div>
                </Field>
              </div>
            )}

            <div style={{ fontSize: 12, color: "var(--ql-fg-subtle)", lineHeight: 1.6 }}>
              Selected: <strong style={{ color: "var(--ql-fg-muted)" }}>{effLang.displayName}</strong> ({effLang.register}) ·{" "}
              {effLang.direction === "rtl" ? "right-to-left" : "left-to-right"}.{" "}
              {isArabicChannel
                ? <>Arabic keeps the original Quotologies pipeline exactly as-is.</>
                : <>The Design step previews sample captions in this language + direction.</>}
              {customOn && <> The preview uses placeholder {effLang.direction === "rtl" ? "right-to-left" : "Latin"} text — your captions render in {effLang.displayName || "your language"}.</>}
            </div>

            {!isArabicChannel && (
              <div style={{
                fontSize: 12.5, color: "var(--ql-fg-muted)", lineHeight: 1.6,
                padding: "12px 14px", borderRadius: 12,
                border: "1px solid rgba(255,213,0,.22)", background: "rgba(255,213,0,.05)",
              }}>
                <strong style={{ color: "var(--ql-fg)" }}>Heads up:</strong> the default caption fonts (AlHurra / Lalezar)
                are Arabic-only. For {scriptName(effLang.code, effLang.direction)} captions, upload a font that covers the
                script on the <strong>Font</strong> step for best results.
              </div>
            )}
          </div>
        )}

        {stepId === "reference" && (
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 300px", gap: 28 }}>
            <div style={{ display: "grid", gap: 16 }}>
              <div style={{ fontSize: 13, color: "var(--ql-fg-muted)", lineHeight: 1.55 }}>
                Optional but recommended: drop a <strong>screenshot of the video or captions you're going for</strong> —
                a frame from an existing post, a competitor's style, anything. It's saved with the channel as your
                <strong> brand style reference</strong>: the look to match as you dial in colors, logo, and captions.
                The live Preview itself uses a neutral sample frame (the same for every channel) so the layout reads
                clearly — your reference is kept on file, not shown as the backdrop.
              </div>
              <div style={{
                display: "flex", gap: 10, alignItems: "flex-start",
                fontSize: 12.5, color: "var(--ql-fg-muted)", lineHeight: 1.55,
                padding: "12px 14px", borderRadius: 12,
                border: "1px solid rgba(255,213,0,.20)", background: "rgba(255,213,0,.05)",
              }}>
                <Sparkles size={16} style={{ color: "var(--ql-yellow)", flexShrink: 0, marginTop: 1 }} />
                <div>
                  <strong style={{ color: "var(--ql-fg)" }}>When you continue, we'll read this frame's styling</strong> and
                  pre-fill your colors, caption layout, logo placement, and font feel from it. Every suggestion is
                  shown as a chip you can tweak or reset — nothing is locked in.
                </div>
              </div>
              <Field label="Reference screenshot (PNG / JPG / WebP, ≤ 8 MiB)">
                <label htmlFor="onb-ref" style={dropStyle}>
                  {refDataUrl
                    ? <img src={refDataUrl} alt="reference" style={{ maxWidth: "70%", maxHeight: 220, objectFit: "contain", borderRadius: 8 }} />
                    : <div style={{ color: "var(--ql-fg-muted)" }}><Upload size={26} style={{ marginBottom: 8 }} /><div>Drop or click to upload a reference frame</div></div>}
                  <input id="onb-ref" type="file" accept="image/png,image/jpeg,image/webp" style={{ display: "none" }}
                    onChange={(e) => void handleReferenceFile(e.target.files?.[0])} />
                </label>
              </Field>
              <Field label="Notes (optional)" hint="What matters about this look — caption placement, mood, font feel, anything to remember.">
                <textarea style={{ ...inputStyle, minHeight: 90, resize: "vertical" }} value={refNotes}
                  placeholder="e.g. big gold punch line at the bottom, calm serif, logo top-centre…"
                  onChange={(e) => setRefNotes(e.target.value)} />
              </Field>
            </div>
            {refDataUrl && (
              <div>
                <div style={{ fontSize: 11, color: "var(--ql-fg-subtle)", marginBottom: 8, textTransform: "uppercase", letterSpacing: ".06em" }}>Your reference</div>
                <img src={refDataUrl} alt="reference" style={{ width: "100%", borderRadius: 14, border: "1px solid rgba(255,255,255,.1)" }} />
                <div style={{ fontSize: 11.5, color: "var(--ql-fg-subtle)", marginTop: 8, lineHeight: 1.5 }}>
                  Saved as this channel's brand style reference — a look to match. It isn't used as the live Preview backdrop.
                </div>
              </div>
            )}
          </div>
        )}

        {stepId === "colors" && (
          <div style={{ display: "grid", gap: 20 }}>
            {matchedPanel(["colors"])}
            {refAnalysisFailed && (
              <div style={{ fontSize: 12, color: "var(--ql-fg-subtle)", lineHeight: 1.5 }}>
                We couldn't read much styling from your reference — starting from the defaults. Adjust anything below.
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 360px", gap: 28 }}>
            <div style={{ display: "grid", gap: 16 }}>
              <ColorField label="Brand accent (UI)" value={accent} onChange={setAccent} />
              <ColorField label="Caption text color" value={captionColor} onChange={setCaptionColor} />
              <ColorField label="Caption highlight / punch" value={captionAccent} onChange={setCaptionAccent}
                hint="Set equal to caption text color for uniform (no highlight) captions." />
              <ColorField label="Speaker name color" value={speakerNameColor} onChange={setSpeakerNameColor} />
              <Field label="Watermark text (optional)">
                <input style={inputStyle} value={watermark} placeholder="@yourbrand" onChange={(e) => setWatermark(e.target.value)} />
              </Field>
            </div>
            <ChannelPreview {...previewProps} />
            </div>
          </div>
        )}

        {stepId === "layout" && (
          <div style={{ display: "grid", gap: 18 }}>
            {matchedPanel(["logo", "captions", "speaker"])}
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Design your captions — in the real Studio</div>
              <div style={{ fontSize: 13, color: "var(--ql-fg-muted)", lineHeight: 1.55 }}>
                This is the SAME editor you'll use on every clip, over a live sample: drag elements
                on the canvas, pick <strong>fonts, colors, outline, shadow, wrap</strong> on the right.
                Everything you set here becomes the channel's design{designSteps > 0 ? ` (${designSteps} step${designSteps === 1 ? "" : "s"} so far)` : ""}.
              </div>
            </div>
            <Field label={logoDataUrl ? "Brand logo — replace (PNG / JPG / WebP, ≤ 4 MiB)" : "Brand logo (PNG / JPG / WebP, ≤ 4 MiB) — required"}>
              <label htmlFor="onb-logo" style={{ ...dropStyle, minHeight: 84, padding: 10 }}>
                {logoDataUrl
                  ? <img src={logoDataUrl} alt="logo" style={{ maxWidth: "40%", maxHeight: 64, objectFit: "contain" }} />
                  : <div style={{ color: "var(--ql-fg-muted)" }}><Upload size={20} style={{ marginBottom: 4 }} /><div>Drop or click to upload</div></div>}
                <input id="onb-logo" type="file" accept="image/png,image/jpeg,image/webp" style={{ display: "none" }}
                  onChange={(e) => void handleLogoFile(e.target.files?.[0])} />
              </label>
            </Field>
            <OnboardingStudio
              // STABLE key: create mode always uses "new" — an effectiveSlug
              // key would orphan the design whenever the operator edits the
              // channel name after designing (critic-caught draft loss).
              draftKey={isEdit && editSlug ? editSlug : "new"}
              inputProps={sampleInputProps}
              onDesignChange={handleDesignChange}
            />
          </div>
        )}

        {stepId === "font" && (
          <div style={{ display: "grid", gap: 16 }}>
            {matchedPanel([], fontSuggestion)}
            <div style={{ fontSize: 13, color: "var(--ql-fg-muted)", lineHeight: 1.55 }}>
              Pick a starting font — every face below ships with the studio, or upload your own.
              You can refine <strong>per element</strong> on the Layout canvas (caption lines and
              speaker card can each use a different one).
            </div>
            {existingFontFamily && !fontFilename && (
              <div style={{ fontSize: 12.5, color: "var(--ql-fg-muted)", lineHeight: 1.6, padding: "10px 14px", borderRadius: 12, border: "1px solid rgba(255,255,255,.10)", background: "rgba(255,255,255,.02)" }}>
                <strong style={{ color: "var(--ql-fg)" }}>Current font: {existingFontFamily}</strong> — kept unless you pick or upload another.
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3, 1fr)", gap: 12 }}>
              {/* Upload tile — your own font joins the gallery */}
              <label htmlFor="onb-font" style={{
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                gap: 8, minHeight: 110, borderRadius: 14, cursor: "pointer", textAlign: "center", padding: 12,
                border: fontFilename ? `1.5px solid ${accent}` : "2px dashed rgba(255,255,255,.14)",
                background: fontFilename ? "rgba(255,213,0,.06)" : "rgba(255,255,255,.02)",
              }}>
                <Type size={20} style={{ color: fontFilename ? accent : "var(--ql-fg-subtle)" }} />
                <div style={{ fontSize: 12.5, fontWeight: 700, color: fontFilename ? "var(--ql-fg)" : "var(--ql-fg-muted)" }}>
                  {fontFilename ? fontFilename : "Upload your font"}
                </div>
                <div style={{ fontSize: 10.5, color: "var(--ql-fg-subtle)" }}>.ttf / .otf</div>
                <input id="onb-font" type="file" accept=".ttf,.otf" style={{ display: "none" }}
                  onChange={(e) => void handleFontFile(e.target.files?.[0])} />
              </label>
              {/* The library — each card renders in its own face; click = use it everywhere */}
              {[...(fontDataUrl && fontFamily ? [{ family: fontFamily, kind: "upload" as const }] : []), ...libraryFamilies].map((f) => {
                const active = fontRoles.caption === f.family && fontRoles.punch === f.family && fontRoles.speaker === f.family;
                return (
                  <button
                    key={f.family}
                    type="button"
                    onClick={() => setFontRoles(active ? { caption: "", punch: "", speaker: "" } : { caption: f.family, punch: f.family, speaker: f.family })}
                    style={{
                      display: "flex", flexDirection: "column", justifyContent: "center", gap: 6,
                      minHeight: 110, borderRadius: 14, cursor: "pointer", padding: "12px 14px", textAlign: "left",
                      fontFamily: "inherit",
                      border: active ? `1.5px solid ${accent}` : "1px solid rgba(255,255,255,.08)",
                      background: active ? "rgba(255,213,0,.07)" : "rgba(255,255,255,.02)",
                    }}
                  >
                    <span style={{ fontFamily: `"${f.family}", sans-serif`, fontSize: 26, lineHeight: 1.15, color: "var(--ql-fg)" }}>
                      {f.family}
                    </span>
                    <span style={{ fontFamily: `"${f.family}", sans-serif`, fontSize: 13, color: "var(--ql-fg-muted)" }}>
                      {effLang.direction === "rtl" ? "تطلب ما تريد 123" : "Your punch line 123"}
                    </span>
                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase", color: active ? accent : "var(--ql-fg-subtle)" }}>
                      {active ? "✓ selected — refine on Layout" : f.kind === "upload" ? "your upload" : f.kind === "founding" ? "founding" : "library"}
                    </span>
                  </button>
                );
              })}
            </div>
            {!isArabicChannel && (
              <div style={{ fontSize: 12, color: "var(--ql-fg-subtle)", lineHeight: 1.55 }}>
                Tip: the founding AlHurra / Lalezar / Faseh cover Arabic only — for {effLang.displayName}, pick a library font or upload one that covers {scriptName(effLang.code, effLang.direction)}.
              </div>
            )}
            {fontFilename && (
              <Field label="Font family name">
                <input style={inputStyle} value={fontFamily} onChange={(e) => setFontFamily(e.target.value.replace(/[^A-Za-z0-9_-]/g, ""))} />
              </Field>
            )}
          </div>
        )}

        {stepId === "features" && (
          <div style={{ display: "grid", gap: 16 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>What your channel does</div>
              <div style={{ fontSize: 13, color: "var(--ql-fg-muted)", lineHeight: 1.55 }}>
                Every channel ships with the full video toolkit — no setup needed. Posts and Originals
                arrive with their own onboarding soon.
              </div>
            </div>

            {/* Primary — the Video pipeline, always included (no opt-in). */}
            <div style={{
              position: "relative", borderRadius: 18, padding: 22,
              border: "1px solid rgba(255,213,0,.22)",
              background: "linear-gradient(135deg, rgba(255,213,0,.06), rgba(255,255,255,.015))",
              boxShadow: "0 0 40px rgba(255,213,0,.05)",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                <div style={pipelineTileStyle(true)}><Video size={22} strokeWidth={1.9} /></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: "-.01em" }}>Video</div>
                  <div style={{ fontSize: 12.5, color: "var(--ql-fg-muted)", marginTop: 3, lineHeight: 1.5 }}>
                    Turn long-form talks into captioned, on-brand vertical clips — ready to publish.
                  </div>
                </div>
                <span style={includedBadgeStyle}><Check size={12} strokeWidth={3} /> Included</span>
              </div>

              <div style={{ height: 1, background: "rgba(255,255,255,.07)", margin: "18px 0 16px" }} />

              <div style={{ display: "grid", gap: 15 }}>
                <Capability icon={Captions} title="Captioned short-form"
                  desc="Long-form talks become vertical 9:16 clips, captioned in your channel's language." />
                <Capability icon={ScanSearch} title="Auto-scan"
                  desc="Point it at a whole video — it fills your Backlog with publish-ready scene candidates." />
                <Capability icon={Search} title="Find a scene"
                  desc="Describe a moment and it's located in the source and cut for you." />
              </div>
            </div>

            {/* Locked — visible but not interactive; own onboarding lands later. */}
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
              <LockedPipelineCard icon={FileImage} title="Posts" desc="On-brand carousels for the feed." />
              <LockedPipelineCard icon={Sparkles} title="Originals" desc="AI-generated video from a prompt." />
            </div>
          </div>
        )}

        {stepId === "voice" && (
          <div style={{ display: "grid", gap: 16 }}>
            {matchedPanel(["voice"])}
            <Field label="Brand voice (optional)">
              <textarea style={{ ...inputStyle, minHeight: 140, resize: "vertical" }} value={voicePromptFragment}
                placeholder="Describe your brand's voice in one paragraph. Shapes Claude prompts (caption tone, etc.)."
                onChange={(e) => setVoicePromptFragment(e.target.value)} />
            </Field>
            <Field
              label="Caption sign-off (optional)"
              hint="Written in your language, appended word-for-word to the end of every video caption (after the source line). Leave empty and captions simply end there — nothing is invented."
            >
              <textarea style={{ ...inputStyle, minHeight: 90, resize: "vertical" }} value={socialCta}
                placeholder={"e.g. Follow us for more, or a line inviting a comment keyword."}
                onChange={(e) => setSocialCta(e.target.value)} />
            </Field>
          </div>
        )}

        {stepId === "buffer" && (
          <div style={{ display: "grid", gap: 16 }}>
            <BufferExplainer />
            <Field
              label="Buffer access token (optional)"
              hint="Leave blank to skip — you can connect this brand's Buffer later in Settings → Scheduling."
            >
              <input
                style={{ ...inputStyle, fontFamily: "var(--ql-font-mono)" }}
                type="password"
                value={bufferToken}
                placeholder="Paste this brand's Buffer access token"
                onChange={(e) => setBufferToken(e.target.value)}
              />
            </Field>
          </div>
        )}

        {stepId === "review" && (() => {
          // The review shows what the payload WILL contain: the wizard's
          // base values with the Design-step edits overlaid (critic-caught
          // — confirming pre-design numbers the submit then overrides
          // would lie to the operator).
          const revBrand = effBrandColors();
          const revRoles = buildFontRolesPayload();
          const revCap = buildLayoutPayload().caption!;
          return (
          <div style={{ display: "grid", gap: 12 }}>
            <Row label="Name" value={displayName} />
            <Row label="Slug" value={effectiveSlug} />
            <Row label="Language" value={`${effLang.displayName} · ${effLang.direction === "rtl" ? "RTL" : "LTR"}${isArabicChannel ? " (original pipeline)" : ""}`} />
            <Row label="Accent" value={accent} swatch={accent} />
            <Row label="Caption text" value={revBrand.captionColor} swatch={revBrand.captionColor} />
            <Row label="Caption highlight" value={revBrand.captionAccent} swatch={revBrand.captionAccent} />
            <Row label="Speaker name" value={revBrand.speakerNameColor} swatch={revBrand.speakerNameColor} />
            <Row label="Logo" value={`${logoFilename ?? "uploaded"} · ${logoWidthPct}% · op ${logoOpacity}`} />
            <Row label="Font" value={
              revRoles
                ? `caption ${revRoles.caption ?? "auto"} · punch ${revRoles.punch ?? "auto"} · speaker ${revRoles.speaker ?? "auto"}`
                : (fontFilename ? `${fontFilename} (${fontFamily})` : "Quotologies defaults")
            } />
            <Row label="Reference" value={
              (refFilename ? `${refFilename}${refNotes.trim() ? " · notes added" : ""}` : (refNotes.trim() ? "notes only" : "none"))
              + (refProfile ? ` · styling read (${refProfile.overallConfidence}), ${matched.length} suggestion${matched.length === 1 ? "" : "s"} applied` : "")
            } />
            <Row label="Captions" value={
              `${revCap.twoLine === null || revCap.captionStyle === "uniform"
                ? `uniform · ${revCap.fontSizePx}px`
                : `two-line · top ${revCap.twoLine?.topPx}px / punch ${revCap.twoLine?.bottomPx}px`}`
              + ` · ${revCap.bottomPx}px up · ${revCap.letterSpacing} tracking`
              + (revCap.box ? ` · box ${revCap.box.opacity}` : "")
              + (revCap.wrapMode === "auto" ? " · auto-wrap" : "")
              + (revCap.karaoke ? ` · karaoke ${revCap.karaoke.mode}` : "")
              + (revCap.animation?.in ? ` · in:${revCap.animation.in}` : "")
              + (revCap.animation?.out ? ` · out:${revCap.animation.out}` : "")
              + (designSteps > 0 ? ` · ${designSteps} design step${designSteps === 1 ? "" : "s"}` : "")
            } />
            <Row label="Pipelines" value="Video (captions + auto-scan + find-a-scene). Posts & Originals coming soon." />
            <Row label="Buffer" value={bufferToken.trim() ? "token entered — will connect on create" : "not connected (add later in Settings)"} />
            {!isArabicChannel && !fontFilename && (
              <div style={{
                marginTop: 4, fontSize: 12.5, color: "var(--ql-fg-muted)", lineHeight: 1.6,
                padding: "12px 14px", borderRadius: 12,
                border: "1px solid rgba(255,213,0,.22)", background: "rgba(255,213,0,.05)",
              }}>
                <strong style={{ color: "var(--ql-fg)" }}>No {scriptName(effLang.code, effLang.direction)} font uploaded.</strong>{" "}
                {effLang.displayName} captions will fall back to the Arabic default fonts. You can still create the
                channel and add a font later in the Font step or Settings.
              </div>
            )}
          </div>
          );
        })()}

        {warning && <div style={{ marginTop: 18, color: "#ffcf5c", fontSize: 13, lineHeight: 1.5 }}>{warning}</div>}
        {error && <div style={{ marginTop: 18, color: "#ff7373", fontSize: 13 }}>{error}</div>}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24 }}>
        <button onClick={back} disabled={stepIdx === 0} style={{
          ...ctaSmallStyle, background: "rgba(255,255,255,.04)", color: "var(--ql-fg-muted)", boxShadow: "none",
          cursor: stepIdx === 0 ? "not-allowed" : "pointer", opacity: stepIdx === 0 ? 0.4 : 1,
        }}><ChevronLeft size={16} /> Back</button>
        {stepId === "review" ? (
          createdSlug ? (
            <button onClick={() => onDone(createdSlug)} style={ctaBtnStyle(true)}>
              Finish <Check size={16} />
            </button>
          ) : (
            <button onClick={() => void submit()} disabled={submitting || !canAdvance} style={ctaBtnStyle(!submitting && canAdvance)}>
              {submitting ? (isEdit ? "Saving…" : "Creating…") : (isEdit ? "Save changes" : "Create channel")} <Check size={16} />
            </button>
          )
        ) : (
          <div style={{ display: "flex", gap: 10 }}>
            {isEdit && !createdSlug && (
              <button
                onClick={() => void submit()}
                disabled={submitting || analyzing}
                title="Save all changes now — no need to click through the remaining steps"
                style={ctaBtnStyle(!submitting && !analyzing)}
              >
                {submitting ? <>Saving… <Loader2 size={16} className="ql-spin" /></> : <>Save changes <Check size={16} /></>}
              </button>
            )}
            <button onClick={() => void handleNext()} disabled={!canAdvance || analyzing} style={ctaBtnStyle(canAdvance && !analyzing)}>
              {analyzing
                ? <>Reading your reference… <Loader2 size={16} className="ql-spin" /></>
                : <>Next <ChevronRight size={16} /></>}
            </button>
          </div>
        )}
      </div>

      {isEdit && editSlug && (() => {
        const nameMatches = archiveTyped.trim().toLowerCase() === (displayName.trim() || editSlug).toLowerCase();
        return (
        <div style={{ ...cardStyle, marginTop: 28 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Archive this brand</div>
          <div style={{ fontSize: 13, color: "var(--ql-fg-muted)", lineHeight: 1.55, marginBottom: 14 }}>
            {displayName.trim() || editSlug} leaves your brand list. Its videos, posts and posting connection stay,
            and one click in Settings › Brand brings it back.
          </div>
          <input
            value={archiveTyped}
            onChange={(e) => setArchiveTyped(e.target.value)}
            placeholder={`Type “${displayName.trim() || editSlug}” to confirm`}
            aria-label="Type the brand name to confirm archiving"
            style={{ ...inputStyle, marginBottom: 10, maxWidth: 360 }}
          />
          {!nameMatches && (
            <div style={{ marginBottom: 10, fontSize: 12, color: "var(--ql-fg-muted)" }}>
              Type the name exactly as shown to enable Archive.
            </div>
          )}
          <div>
            <button
              type="button"
              style={{ ...ctaSmallStyle, opacity: nameMatches ? 1 : 0.45, cursor: nameMatches ? "pointer" : "not-allowed" }}
              disabled={!nameMatches}
              onClick={() => { setArchiveError(null); setArchiveOpen(true); }}
            >
              Archive brand…
            </button>
          </div>
          {archiveError && <div role="alert" style={{ marginTop: 10, color: "#ff7373", fontSize: 13 }}>{archiveError}</div>}
          <ConfirmDialog
            open={archiveOpen}
            title={`Archive ${displayName.trim() || editSlug}?`}
            message="Everything stays and can be restored from Settings › Brand."
            confirmLabel="Archive"
            danger
            busy={archiving}
            onCancel={() => setArchiveOpen(false)}
            onConfirm={() => {
              if (!nameMatches) { setArchiveOpen(false); return; }
              setArchiving(true);
              void archiveChannelApi(editSlug)
                .then(() => { window.location.hash = "#/settings"; window.location.reload(); })
                .catch((err: unknown) => { setArchiveError(err instanceof Error ? err.message : String(err)); setArchiving(false); setArchiveOpen(false); });
            }}
          />
        </div>
        );
      })()}
    </div>
  );
}

const dropStyle: React.CSSProperties = {
  display: "block", border: "2px dashed rgba(255,255,255,.12)", borderRadius: 18, padding: 32,
  textAlign: "center", cursor: "pointer", background: "rgba(255,255,255,.02)",
};

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div>
      <div style={fieldLabelStyle}>{label}</div>
      {children}
      {hint && <div style={{ fontSize: 12, color: "var(--ql-fg-muted)", marginTop: 6 }}>{hint}</div>}
    </div>
  );
}

function ColorField({ label, value, onChange, hint }: { label: string; value: string; onChange: (v: string) => void; hint?: string }): React.JSX.Element {
  return (
    <Field label={label} hint={hint}>
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)}
          style={{ width: 42, height: 42, padding: 0, border: "none", background: "transparent", cursor: "pointer" }} />
        <input style={{ ...inputStyle, width: 130, fontFamily: "var(--ql-font-mono)" }} value={value} onChange={(e) => onChange(e.target.value)} />
      </div>
    </Field>
  );
}

/**
 * "Matched from your reference" — the advisory panel shown at the top of a
 * styling step. Lists every field the reference-vision pass pre-filled within
 * the given scopes as a resettable chip, plus a scope-wide Reset. Renders null
 * when nothing in scope was matched and there's no note (e.g. the font step's
 * suggestion). Never a silent overwrite — this is how the operator SEES it.
 */
function MatchedPanel({ scopes, matched, note, confidence, accent, onResetOne, onResetScopes }: {
  scopes: MatchScope[];
  matched: MatchedField[];
  note: string | null;
  confidence: "high" | "medium" | "low" | null;
  accent: string;
  onResetOne: (key: string) => void;
  onResetScopes: (scopes: MatchScope[]) => void;
}): React.JSX.Element | null {
  const items = matched.filter((m) => scopes.includes(m.scope));
  if (items.length === 0 && !note) return null;

  const conf = confidence ?? "medium";
  const confColor = conf === "high" ? "#4ade80" : conf === "low" ? "var(--ql-fg-subtle)" : "var(--ql-yellow)";
  const confBg = conf === "high" ? "rgba(74,222,128,.14)" : conf === "low" ? "rgba(255,255,255,.06)" : "rgba(255,213,0,.14)";

  return (
    <div style={{
      display: "grid", gap: 12, padding: "14px 16px", borderRadius: 14,
      border: `1px solid ${hexA(accent, 0.2)}`, background: hexA(accent, 0.04),
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <Sparkles size={15} style={{ color: "var(--ql-yellow)", flexShrink: 0 }} />
        <span style={{ fontSize: 13, fontWeight: 700, color: "var(--ql-fg)" }}>Matched from your reference</span>
        {confidence && (
          <span style={{
            fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 999,
            color: confColor, background: confBg, textTransform: "uppercase", letterSpacing: ".05em",
          }}>{conf} confidence</span>
        )}
        {items.length > 0 && (
          <button type="button" onClick={() => onResetScopes(scopes)} style={{
            marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 5,
            fontSize: 11.5, fontFamily: "inherit", cursor: "pointer",
            padding: "4px 9px", borderRadius: 8, color: "var(--ql-fg-muted)",
            border: "1px solid rgba(255,255,255,.10)", background: "rgba(255,255,255,.02)",
          }}>
            <RotateCcw size={12} /> Reset
          </button>
        )}
      </div>

      {items.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {items.map((m) => (
            <span key={m.key} style={{
              display: "inline-flex", alignItems: "center", gap: 7,
              padding: "5px 8px 5px 10px", borderRadius: 999, fontSize: 12,
              border: "1px solid rgba(255,255,255,.10)", background: "rgba(255,255,255,.03)",
            }}>
              {m.swatch && <span style={{ width: 12, height: 12, borderRadius: 4, background: m.swatch, border: "1px solid rgba(255,255,255,.2)", flexShrink: 0 }} />}
              <span style={{ color: "var(--ql-fg-muted)" }}>{m.label}</span>
              <span style={{ color: "var(--ql-fg)", fontFamily: m.swatch ? "var(--ql-font-mono)" : "inherit" }}>{m.detected}</span>
              <button type="button" aria-label={`Reset ${m.label}`} onClick={() => onResetOne(m.key)} style={{
                display: "inline-flex", cursor: "pointer", border: "none", background: "transparent",
                color: "var(--ql-fg-subtle)", padding: 0, marginLeft: 1,
              }}>
                <X size={13} />
              </button>
            </span>
          ))}
        </div>
      )}

      {note && (
        <div style={{ fontSize: 12, color: "var(--ql-fg-muted)", lineHeight: 1.5, display: "flex", gap: 8, alignItems: "flex-start" }}>
          <Type size={13} style={{ color: "var(--ql-fg-subtle)", flexShrink: 0, marginTop: 2 }} />
          <span>{note}</span>
        </div>
      )}
    </div>
  );
}

/** Hex + alpha → rgba() for panel chrome tinted to the brand accent. */
function hexA(hex: string, a: number): string {
  const m = hex.replace("#", "");
  const n = m.length === 3 ? m.split("").map((c) => c + c).join("") : m;
  const r = parseInt(n.slice(0, 2), 16) || 0;
  const g = parseInt(n.slice(2, 4), 16) || 0;
  const b = parseInt(n.slice(4, 6), 16) || 0;
  return `rgba(${r},${g},${b},${a})`;
}

/** Rounded icon tile for a pipeline card. `gold` = the included Video pipeline;
 *  otherwise a quiet neutral tile (locked cards). */
function pipelineTileStyle(gold: boolean): React.CSSProperties {
  return {
    width: 46, height: 46, borderRadius: 13, flexShrink: 0,
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    color: gold ? "var(--ql-yellow)" : "var(--ql-fg-muted)",
    background: gold
      ? "linear-gradient(135deg, rgba(255,213,0,.18), rgba(255,165,0,.07))"
      : "rgba(255,255,255,.04)",
    border: "1px solid " + (gold ? "rgba(255,213,0,.28)" : "rgba(255,255,255,.08)"),
  };
}

/** Gold "Included" pill — signals the video toolkit is on by default, no
 *  checkbox to tick. */
const includedBadgeStyle: React.CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap",
  fontSize: 11, fontWeight: 800, padding: "5px 11px", borderRadius: 999,
  background: "rgba(255,213,0,.14)", color: "var(--ql-yellow)",
  border: "1px solid rgba(255,213,0,.30)",
  textTransform: "uppercase", letterSpacing: ".05em",
};

/** One capability row inside the Video card (captions / auto-scan / find-a-scene). */
function Capability({ icon: Icon, title, desc }: { icon: typeof Video; title: string; desc: string }): React.JSX.Element {
  return (
    <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
      <div style={{
        width: 32, height: 32, borderRadius: 9, flexShrink: 0, marginTop: 1,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        color: "var(--ql-yellow)", background: "rgba(255,213,0,.08)",
        border: "1px solid rgba(255,213,0,.16)",
      }}>
        <Icon size={16} strokeWidth={1.9} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700 }}>{title}</div>
        <div style={{ fontSize: 12, color: "var(--ql-fg-muted)", lineHeight: 1.5, marginTop: 2 }}>{desc}</div>
      </div>
    </div>
  );
}

/** A dimmed, non-interactive "coming soon" pipeline card — mirrors the
 *  Sidebar's locked-nav pattern (dim + Lock + "Soon" pill). */
function LockedPipelineCard({ icon: Icon, title, desc }: { icon: typeof Video; title: string; desc: string }): React.JSX.Element {
  return (
    <div style={{
      display: "flex", flexDirection: "column", gap: 10, padding: 18, borderRadius: 16,
      border: "1px solid rgba(255,255,255,.06)", background: "rgba(255,255,255,.015)",
      opacity: 0.6, cursor: "default",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{
          width: 38, height: 38, borderRadius: 11, flexShrink: 0,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          color: "var(--ql-fg-muted)", background: "rgba(255,255,255,.04)",
          border: "1px solid rgba(255,255,255,.08)",
        }}>
          <Icon size={18} strokeWidth={1.75} />
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, flex: 1, minWidth: 0 }}>{title}</div>
        <Lock size={13} strokeWidth={2} style={{ color: "var(--ql-fg-subtle)", flexShrink: 0 }} />
        <span style={{
          fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 999, whiteSpace: "nowrap",
          background: "rgba(255,255,255,.06)", color: "var(--ql-fg-muted)",
          textTransform: "uppercase", letterSpacing: ".05em",
        }}>Soon</span>
      </div>
      <div style={{ fontSize: 12, color: "var(--ql-fg-muted)", lineHeight: 1.5 }}>
        {desc} <span style={{ color: "var(--ql-fg-subtle)" }}>Gets its own setup later.</span>
      </div>
    </div>
  );
}

function Row({ label, value, swatch }: { label: string; value: string; swatch?: string }): React.JSX.Element {
  const isMobile = useIsMobile();
  return (
    <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? 4 : 16, alignItems: isMobile ? "flex-start" : "center", paddingBottom: 10, borderBottom: "1px solid rgba(255,255,255,.05)" }}>
      <div style={{ width: isMobile ? "auto" : 140, fontSize: 12, color: "var(--ql-fg-muted)", textTransform: "uppercase", letterSpacing: ".06em" }}>{label}</div>
      <div style={{ flex: 1, fontSize: 14, fontFamily: swatch ? "var(--ql-font-mono)" : "inherit", wordBreak: "break-word" }}>{value}</div>
      {swatch && <div style={{ width: 28, height: 28, borderRadius: 8, background: swatch, border: "1px solid rgba(255,255,255,.1)", flexShrink: 0 }} />}
    </div>
  );
}
